/**
 * SecureEmbed Energy Portal - Report Storage Service
 *
 * localStorage-backed storage for generated reports
 * Supports multi-client workspace scoping and report branding
 */

export interface StoredReport {
  id: string;
  title: string;
  type: string;
  timestamp: string;
  data: any;
  summary?: string;
  workspaceId?: string;
}

export interface ReportBranding {
  orgName: string;
  logoBase64: string;
  footerText: string;
}

export interface Workspace {
  id: string;
  name: string;
  branding: ReportBranding;
  createdAt: string;
}

const STORAGE_KEY = 'secureembed_reports';
const WORKSPACE_KEY = 'secureembed_workspaces';
const ACTIVE_WORKSPACE_KEY = 'secureembed_active_workspace';
const MAX_REPORTS = 50;

function getReports(): StoredReport[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

function saveReports(reports: StoredReport[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(reports.slice(0, MAX_REPORTS)));
}

export const reportStorage = {
  list: (workspaceId?: string): StoredReport[] => {
    const all = getReports().sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    if (workspaceId) return all.filter(r => r.workspaceId === workspaceId);
    return all;
  },

  save: (report: Omit<StoredReport, 'id' | 'timestamp'>): StoredReport => {
    const stored: StoredReport = {
      ...report,
      id: `rpt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      timestamp: new Date().toISOString(),
    };
    const reports = getReports();
    reports.unshift(stored);
    saveReports(reports);
    return stored;
  },

  get: (id: string): StoredReport | null => {
    return getReports().find(r => r.id === id) || null;
  },

  remove: (id: string): void => {
    const reports = getReports().filter(r => r.id !== id);
    saveReports(reports);
  },

  clear: (): void => {
    localStorage.removeItem(STORAGE_KEY);
  },
};

export const workspaceStorage = {
  list: (): Workspace[] => {
    try {
      const raw = localStorage.getItem(WORKSPACE_KEY);
      if (!raw) return [];
      return JSON.parse(raw);
    } catch {
      return [];
    }
  },

  save: (workspace: Omit<Workspace, 'id' | 'createdAt'>): Workspace => {
    const stored: Workspace = {
      ...workspace,
      id: `ws_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      createdAt: new Date().toISOString(),
    };
    const workspaces = workspaceStorage.list();
    workspaces.push(stored);
    localStorage.setItem(WORKSPACE_KEY, JSON.stringify(workspaces));
    return stored;
  },

  remove: (id: string): void => {
    const workspaces = workspaceStorage.list().filter(w => w.id !== id);
    localStorage.setItem(WORKSPACE_KEY, JSON.stringify(workspaces));
  },

  getActiveId: (): string | null => {
    return localStorage.getItem(ACTIVE_WORKSPACE_KEY);
  },

  setActiveId: (id: string | null): void => {
    if (id) {
      localStorage.setItem(ACTIVE_WORKSPACE_KEY, id);
    } else {
      localStorage.removeItem(ACTIVE_WORKSPACE_KEY);
    }
  },

  getActive: (): Workspace | null => {
    const id = workspaceStorage.getActiveId();
    if (!id) return null;
    return workspaceStorage.list().find(w => w.id === id) || null;
  },
};
